/** Combat! The tunnel screen */
import kontra from 'kontra';

import BaseScene from './base';
import { SCREEN_WIDTH, SCREEN_HEIGHT, ROWS_PER_TRAVEL } from '../constants';

import uiTexture from '../../assets/ui.png';
import { Card, CardType } from '../entities/card';
import Particles from '../particles';

const { keyPressed, emit, track, untrack, imageAssets, on, off, Sprite } = kontra;

export default class TitleScene extends BaseScene {
  constructor () {
    super();
    this.uiPattern = this.context.createPattern(imageAssets[uiTexture], 'repeat');
    this.infoPanel = document.querySelector('.tunnel-info');
    this.playerHealth = document.querySelector('.engineer-bar');
    this.networkHealth = document.querySelector('.network-bar');
    this.playerHealthValue = 20;
    this.networkHealthValue = 100;

    this.instructionPanel = document.querySelector('.tunnel-instructions');
    this.instructionButton = this.instructionPanel.querySelector('button');
    this.hasHiddenInstructions = false;

    this.instructionButton.addEventListener('click', () => {
      this.hasHiddenInstructions = true;
      this.instructionPanel.classList.remove('visible');
    });
  }

  onEnter () {
    if (!this.hasHiddenInstructions) {
      this.instructionPanel.classList.add('visible');
    }

    this.infoPanel.classList.add('visible');
    // Initialize an m x n grid of cards to make choices through
    this.cards = [];
    for (let i = 0; i < ROWS_PER_TRAVEL; i++) {
      this.cards.push([]);
      for (let j = 0; j < 4; j++) {
        const sprite = new Card({ row: i, column: j });
        track(sprite);
        this.cards[i].push(sprite);
      }
    }

    for (let i = 0; i < Math.min(ROWS_PER_TRAVEL, this.cards.length); i++) {
      this.cards[i].map((card, index) => {
        card.x = 460 + index * 240;
        card.y = 60 + i * 216;
      });
    }

    // middle left cursor
    this.cursor = 1;
    emit('cursor-move', this.cursor);

    this.boundCardClick = this.doCardClick.bind(this);
    this.boundCardEffect = this.doCardEffect.bind(this);
    this.boundSetHealth = this.setHealth.bind(this);

    on('card-click', this.boundCardClick);
    on('card-effect', this.boundCardEffect);
    on('health', this.boundSetHealth);
  }

  setHealth ({ player, network }) {
    this.playerHealthValue = player;
    this.networkHealthValue = network;
  }

  onExit () {
    this.infoPanel.classList.remove('visible');
    off('card-effect', this.boundCardEffect);
    off('card-click', this.boundCardClick);
    off('health', this.boundSetHealth);
  }

  doCardEffect (cardType) {
    // Get rid of top row completely
    const oldCards = this.cards.shift();
    oldCards.map(c => untrack(c));

    this.cards.map(row => row.map(card => { card.promote(); }));

    if (this.cards.length === 0) {
      setTimeout(() => {
        emit('scene-change', 'overworld');
        emit('overworld-health', { player: this.playerHealthValue, link: this.networkHealthValue });
      }, 500);
    }

    this.cardTypeToEffect(cardType);
  }

  cardTypeToEffect (cardType) {
    switch (cardType) {
      case CardType.SHIELD: {
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 20,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1] },
          contents: '+2'
        });
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 140,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1] },
          contents: '+4'
        });
        this.playerHealthValue = Math.min(20, this.playerHealthValue + 2);
        this.networkHealthValue = Math.min(100, this.networkHealthValue + 4);
        break;
      }
      case CardType.HEALTH: {
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 20,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1] },
          contents: '+5'
        });
        this.playerHealthValue = Math.min(20, this.playerHealthValue + 4);
        break;
      }
      case CardType.SPANNER: {
        const healValue = 8 + Math.floor(Math.random() * 10);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 140,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1] },
          contents: `+${healValue}`
        });
        this.networkHealthValue = Math.min(100, this.networkHealthValue + healValue);
        break;
      }
      case CardType.OK: {
        const healValue = 2 + Math.floor(Math.random() * 3);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 140,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1] },
          contents: `+${healValue}`
        });
        this.networkHealthValue = Math.min(100, this.networkHealthValue + healValue);
        break;
      }
      case CardType.SERVER_ERROR: {
        const harmValue = 10 + Math.floor(Math.random() * 10);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 140,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1], color: '#eee' },
          contents: `-${harmValue}`
        });
        this.networkHealthValue = Math.max(this.networkHealthValue - harmValue, 0);
        break;
      }
      case CardType.NOT_FOUND: {
        const harmValue = 5 + Math.floor(Math.random() * 5);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 140,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1], color: '#eee' },
          contents: `-${harmValue}`
        });
        this.networkHealthValue = Math.max(this.networkHealthValue - harmValue, 0);
        break;
      }
      case CardType.SPIDER: {
        const harmValue = 3 + Math.floor(Math.random() * 3);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 20,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1], color: '#eee' },
          contents: `-${harmValue}`
        });
        this.playerHealthValue = Math.max(this.playerHealthValue - harmValue, 0);
        break;
      }
      case CardType.ROBOT: {
        const harmValue = 4 + Math.floor(Math.random() * 4);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 20,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1], color: '#eee' },
          contents: `-${harmValue}`
        });
        this.playerHealthValue = Math.max(this.playerHealthValue - harmValue, 0);
        break;
      }
      case CardType.WORM: {
        const harmValue = 4 + Math.floor(Math.random() * 6);
        Particles.addParticle({
          ttl: 1000,
          position: {
            x: 20,
            y: 200
          },
          velocity: { y: -20 },
          style: { opacity: [1, 0], scale: [2, 1, 1, 1], color: '#eee' },
          contents: `-${harmValue}`
        });
        this.playerHealthValue = Math.max(this.playerHealthValue - harmValue, 0);
        break;
      }
      default: {
        console.log('missing card effect');
        break;
      }
    }

    // One kind of gameover
    if (this.playerHealthValue <= 0) {
      setTimeout(() => {
        emit('scene-change', 'hero');
      }, 1000);
    }

    if (this.networkHealthValue <= 0) {
      setTimeout(() => {
        emit('link-died');
        emit('scene-change', 'overworld');
      }, 1000);
    }

    // The other kind of gameover
  }

  doCardClick ({ row, column }) {
    this.cards[row].map(c => c.leave());
    this.cards[row][column].stay();
    this.cursor = column;
    emit('cursor-move', column);
  }

  update () {
    this.cards.map(row => row.map(c => c.update()));
  }

  renderBackground () {
    this.context.save();
    this.context.fillStyle = this.uiPattern;
    this.context.fillRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);

    this.context.fillStyle = 'rgb(33, 33, 33)';
    this.context.fillRect(360, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
    this.context.fillStyle = 'rgb(11, 11, 11)';
    this.context.fillRect(360, 0, SCREEN_WIDTH, 272);
  }

  renderCards () {
    this.context.fillStyle = '#aaaaee';
    for (let i = 0; i < Math.min(4, this.cards.length); i++) {
      this.cards[i].map((card, index) => {
        card.render();
      });
    }
  }

  render () {
    this.renderBackground();
    this.renderCards();
    this.playerHealth.style.height = `${112 * (this.playerHealthValue / 20)}px`;
    this.networkHealth.style.height = `${112 * (this.networkHealthValue / 100)}px`;
  }
}
